import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ExternalLink, Loader2, RotateCcw, EyeOff } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const STATES = {
  scheduled: { de: 'Geplant', en: 'Scheduled', cls: 'bg-[#00D9C8]/20 text-[#00D9C8]' },
  processing: { de: 'Wird verarbeitet', en: 'Processing', cls: 'bg-blue-500/20 text-blue-300' },
  committed: { de: 'GitHub-Commit erstellt', en: 'GitHub commit created', cls: 'bg-blue-500/20 text-blue-300' },
  building: { de: 'Build läuft', en: 'Build running', cls: 'bg-blue-500/20 text-blue-300' },
  verifying: { de: 'URL wird geprüft', en: 'Checking URL', cls: 'bg-blue-500/20 text-blue-300' },
  published: { de: 'Veröffentlicht', en: 'Published', cls: 'bg-green-500/20 text-green-400' },
  unpublished: { de: 'Zurückgezogen', en: 'Unpublished', cls: 'bg-slate-500/20 text-slate-300' },
  failed: { de: 'Fehlgeschlagen', en: 'Failed', cls: 'bg-red-500/20 text-red-400' },
  build_not_started: { de: 'Build nicht gestartet', en: 'Build not started', cls: 'bg-red-500/20 text-red-400' },
  cancelled: { de: 'Abgebrochen', en: 'Cancelled', cls: 'bg-slate-500/20 text-slate-300' },
};
const STAGES = {
  export: { de: 'JSON-Export', en: 'JSON export' },
  validation: { de: 'JSON-Prüfung', en: 'JSON validation' },
  image: { de: 'Bild', en: 'Image' },
  github_commit: { de: 'GitHub-Commit', en: 'GitHub commit' },
  permission: { de: 'Berechtigung', en: 'Permission' },
  build: { de: 'Cloudflare-Build', en: 'Cloudflare build' },
  url_check: { de: 'URL-Prüfung', en: 'URL check' },
};
const ACTIVE = ['scheduled', 'processing', 'committed', 'building', 'verifying'];

const Row = ({ label, children, testid }) => (
  <div className="flex justify-between gap-3 text-xs" data-testid={testid}>
    <span className="text-[#5A6C7D]">{label}</span>
    <span className="text-white text-right break-all">{children}</span>
  </div>
);

export const GithubPublishStatus = ({ eventId, lang, getAuthHeader, onChanged }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const de = lang === 'de';

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/website-publishing/events/${eventId}`, { headers: getAuthHeader() });
      setInfo(data);
    } catch (_e) {
      setInfo({ error: true });
    }
  }, [eventId, getAuthHeader]);

  useEffect(() => { load(); }, [load]);
  const state = info?.event?.website_publish_state;
  useEffect(() => {
    if (!ACTIVE.includes(state)) return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [state, load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (de ? 'Aktion fehlgeschlagen' : 'Action failed'));
    } finally {
      setBusy(false);
    }
  };

  if (!info) return <Loader2 className="w-4 h-4 animate-spin text-[#5A6C7D]" />;
  if (info.error) return <p className="text-xs text-red-300">{de ? 'Status nicht ladbar.' : 'Could not load status.'}</p>;
  const ev = info.event;
  const s = STATES[state] || STATES.scheduled;
  const stage = STAGES[ev.website_stage];
  const canRetry = ['failed', 'build_not_started'].includes(state);
  const canUnpublish = info.article?.status === 'published' && !ACTIVE.includes(state);

  return (
    <div className="space-y-2" data-testid="github-publish-status">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className={s.cls} data-testid="github-publish-state">{de ? s.de : s.en}</Badge>
        {info.target?.is_staging && <Badge className="bg-amber-500/20 text-amber-300">Staging · {info.target.branch}</Badge>}
      </div>
      <Row label={de ? 'Website' : 'Website'} testid="github-publish-target">{info.target?.name}</Row>
      <Row label={de ? 'Quellprojekt' : 'Source project'} testid="github-publish-project">{info.project_name || '–'}</Row>
      <Row label={de ? 'Sprache' : 'Language'}>{(ev.website_lang || '').toUpperCase()}</Row>
      <Row label={de ? 'Aktion' : 'Action'}>{ev.website_action === 'unpublish' ? (de ? 'Zurückziehen' : 'Unpublish') : (de ? 'Veröffentlichen/Aktualisieren' : 'Publish/Update')}</Row>
      {ev.github_commit_sha && <Row label="Commit" testid="github-publish-commit">{ev.github_commit_sha.slice(0, 7)}</Row>}
      {ev.cf_version_id && <Row label="Cloudflare-Version" testid="github-publish-version">{ev.cf_version_id}</Row>}
      {ev.cf_build_id && <Row label="Cloudflare-Build-ID" testid="github-publish-build-id">{ev.cf_build_id}</Row>}
      {ev.website_url && (
        <a href={ev.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-green-400 hover:text-green-300 underline inline-flex items-center gap-1" data-testid="github-publish-url">
          {ev.website_url} <ExternalLink className="w-3 h-3" />
        </a>
      )}
      {ev.website_error && (
        <div className="rounded border border-red-500/30 bg-red-500/10 p-2" data-testid="github-publish-error">
          {stage && <p className="text-[11px] font-semibold text-red-300">{de ? 'Fehler bei' : 'Error at'}: {de ? stage.de : stage.en}</p>}
          <p className="text-xs text-red-300/90">{ev.website_error}</p>
          {ev.website_publish_state === 'scheduled' && ev.website_next_attempt_at && (
            <p className="text-[11px] text-[#A0AEC0] mt-1">{de ? 'Automatischer neuer Versuch geplant.' : 'Automatic retry scheduled.'}</p>
          )}
        </div>
      )}
      <div className="flex gap-2 flex-wrap pt-1">
        {canRetry && (
          <Button size="sm" disabled={busy} onClick={() => act(() => axios.post(`${API}/website-publishing/events/${eventId}/retry`, {}, { headers: getAuthHeader() }), de ? 'Erneut angestoßen' : 'Retry started')}
            className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00D9C8]/90" data-testid="github-publish-retry-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1" />{de ? 'Erneut versuchen' : 'Retry'}
          </Button>
        )}
        {canUnpublish && (
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => window.confirm(de ? 'Artikel wirklich von der Website zurückziehen?' : 'Unpublish this article?') && act(() => axios.post(`${API}/website-publishing/content/${ev.content_piece_id}/unpublish`, { target_id: info.target.id }, { headers: getAuthHeader() }), de ? 'Zurückziehen eingeplant' : 'Unpublish scheduled')}
            className="border-[#1A2B42] text-[#A0AEC0]" data-testid="github-publish-unpublish-btn">
            <EyeOff className="w-3.5 h-3.5 mr-1" />{de ? 'Zurückziehen' : 'Unpublish'}
          </Button>
        )}
      </div>
    </div>
  );
};
